import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ReactFlow, { Background, Controls, ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { API, formatApiError } from "@/lib/api";
import { PAGE, CARD, ICON_BTN } from "@/lib/styles";
import { FileText, Download, Eye, X, Loader2, FolderOpen, Lightbulb, Table2 } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { computeSheet, colName } from "@/lib/sheetEngine";
import { EmbeddedSheet } from "@/components/EmbeddedSheet";

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Header({ owner }) {
  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800 h-16 flex items-center justify-between px-4 md:px-8">
      <a href="/" className="flex items-center gap-2.5">
        <img src="/icon-192.png" alt="Notacora" className="w-8 h-8 rounded-lg" />
        <span className="font-heading font-semibold text-lg tracking-tight">Notacora</span>
      </a>
      <span className="font-mono2 text-[11px] text-slate-400">{owner ? `Compartido por ${owner}` : "Vista pública"}</span>
    </nav>
  );
}

function MindMapView({ mindmap }) {
  const nodes = (mindmap?.nodes || []).map((n) => ({ ...n, type: "default", draggable: false, connectable: false }));
  const edges = mindmap?.edges || [];
  return (
    <div className="h-[70vh] rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="public-mindmap">
      <ReactFlowProvider>
        <ReactFlow nodes={nodes} edges={edges} fitView nodesDraggable={false} nodesConnectable={false} elementsSelectable={false} minZoom={0.2} maxZoom={3} proOptions={{ hideAttribution: true }}>
          <Background gap={22} size={1.5} color="#94a3b8" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

function SheetView({ cells }) {
  const { display } = computeSheet(Array.isArray(cells) && cells.length ? cells : [[""]]);
  const ncols = display[0]?.length || 0;
  return (
    <div className="overflow-auto border border-slate-200 dark:border-slate-800 rounded-xl" data-testid="public-sheet">
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-10"></th>
            {Array.from({ length: ncols }, (_, c) => (
              <th key={c} className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 min-w-[90px] px-2 py-1.5">{colName(c)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {display.map((row, r) => (
            <tr key={r}>
              <td className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 text-center">{r + 1}</td>
              {row.map((v, c) => (
                <td key={c} className="border border-slate-200 dark:border-slate-800 px-2 py-1.5 font-mono2 text-[13px] text-slate-800 dark:text-slate-200 min-w-[90px]">{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PUB_MD_COMPONENTS = {
  pre({ children }) {
    const child = Array.isArray(children) ? children[0] : children;
    const cls = child?.props?.className || "";
    if (typeof cls === "string" && cls.includes("notacora-sheet")) return <>{children}</>;
    return <pre>{children}</pre>;
  },
  code({ className, children, ...props }) {
    if (typeof className === "string" && className.includes("notacora-sheet")) {
      return <EmbeddedSheet raw={String(children)} />;
    }
    return <code className={className} {...props}>{children}</code>;
  },
  div({ node, children, ...props }) {
    const attr = node?.properties?.dataNotacoraSheet;
    if (attr) return <EmbeddedSheet raw={String(attr)} />;
    return <div {...props}>{children}</div>;
  },
};

export default function PublicView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null); // {url, filename}

  useEffect(() => {
    axios
      .get(`${API}/public/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [token]);

  const dlUrl = (docId) => `${API}/public/${token}/download${docId ? `?doc_id=${docId}` : ""}`;

  if (loading) {
    return (
      <div className={`${PAGE} font-body flex items-center justify-center`}>
        <Loader2 className="animate-spin text-slate-400" size={26} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${PAGE} font-body`}>
        <Header />
        <div className="max-w-md mx-auto p-10 text-center mt-16">
          <div className="w-14 h-14 rounded-2xl bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4"><X size={26} /></div>
          <h1 className="font-heading font-normal text-2xl mb-2">Enlace no disponible</h1>
          <p className="font-body text-sm text-slate-500 dark:text-slate-400" data-testid="public-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${PAGE} font-body`}>
      <Header owner={data.owner_name} />
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        {/* NOTE (text) */}
        {data.type === "note" && data.note_type !== "mindmap" && data.note_type !== "sheet" && (
          <article data-testid="public-note">
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight mb-3">{data.title}</h1>
            <p className="font-mono2 text-xs text-slate-400 mb-8">
              {data.updated_at ? format(new Date(data.updated_at), "d 'de' MMMM yyyy", { locale: es }) : ""}
            </p>
            <div className="prose prose-slate dark:prose-invert max-w-none [&_h1]:font-heading [&_h2]:font-heading [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-indigo-500">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={PUB_MD_COMPONENTS}>{data.content || "*Sin contenido*"}</ReactMarkdown>
            </div>
          </article>
        )}

        {/* MINDMAP */}
        {data.type === "note" && data.note_type === "mindmap" && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Lightbulb size={22} className="text-indigo-500" />
              <h1 className="font-heading font-light text-3xl sm:text-4xl tracking-tight">{data.title}</h1>
            </div>
            <MindMapView mindmap={data.mindmap} />
          </div>
        )}

        {/* SHEET */}
        {data.type === "note" && data.note_type === "sheet" && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Table2 size={22} className="text-emerald-500" />
              <h1 className="font-heading font-light text-3xl sm:text-4xl tracking-tight">{data.title}</h1>
            </div>
            <SheetView cells={data.sheet?.cells} />
          </div>
        )}

        {/* DOCUMENT */}
        {data.type === "document" && (
          <div data-testid="public-document">
            <div className="flex items-center gap-2 mb-6">
              <FileText size={22} className="text-rose-500" />
              <h1 className="font-heading font-light text-3xl sm:text-4xl tracking-tight break-words">{data.filename}</h1>
            </div>
            <div className="flex gap-3 mb-6">
              <a href={dlUrl()} download={data.filename} className={ICON_BTN} data-testid="public-doc-download"><Download size={16} /> <span className="text-sm">Descargar</span></a>
              <span className="font-mono2 text-xs text-slate-400 self-center">{fmtSize(data.size)}</span>
            </div>
            <iframe title={data.filename} src={dlUrl()} className="w-full h-[75vh] rounded-xl border border-slate-200 dark:border-slate-800 bg-white" data-testid="public-doc-iframe" />
          </div>
        )}

        {/* FOLDER */}
        {data.type === "folder" && (
          <div data-testid="public-folder">
            <div className="flex items-center gap-2 mb-8">
              <FolderOpen size={22} className="text-indigo-500" />
              <h1 className="font-heading font-light text-3xl sm:text-4xl tracking-tight">{data.name}</h1>
            </div>
            {data.files.length === 0 ? (
              <p className="font-body text-sm text-slate-500 dark:text-slate-400">Esta carpeta está vacía.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.files.map((d) => (
                  <div key={d.id} className={`${CARD} p-4 flex flex-col gap-3 group`} data-testid={`public-file-${d.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center"><FileText size={20} /></div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                        <button onClick={() => setViewer({ url: dlUrl(d.id), filename: d.filename })} className={ICON_BTN}><Eye size={14} /></button>
                        <a href={dlUrl(d.id)} download={d.filename} className={ICON_BTN}><Download size={14} /></a>
                      </div>
                    </div>
                    <h3 className="font-heading font-medium text-sm break-words leading-snug">{d.filename}</h3>
                    <p className="font-mono2 text-[10px] text-slate-400 mt-auto">{fmtSize(d.size)} · {d.created_at ? format(new Date(d.created_at), "d 'de' MMMM yyyy", { locale: es }) : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA para no registrados */}
      <div className="border-t border-slate-200 dark:border-slate-800 mt-8" data-testid="public-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-600/10 text-indigo-500 flex items-center justify-center"><Lightbulb size={22} /></div>
            <div>
              <p className="font-heading font-medium text-base">Crea tu propia nota gratis en Notacora</p>
              <p className="font-body text-sm text-slate-500 dark:text-slate-400">Empieza con 3 tazas de café de regalo ☕ (3 meses gratis).</p>
            </div>
          </div>
          <a href="/register" className={`${ICON_BTN} bg-indigo-600 hover:bg-indigo-500 text-white border-transparent px-5 py-2.5`} data-testid="public-cta-register">
            <span className="text-sm font-medium">Empezar gratis</span>
          </a>
        </div>
      </div>

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex flex-col p-4 md:p-8">
          <div className="flex items-center justify-between mb-3 text-white">
            <h3 className="font-heading font-medium text-sm truncate">{viewer.filename}</h3>
            <button onClick={() => setViewer(null)} className="border border-white/30 rounded-lg p-2 hover:bg-white/10"><X size={18} /></button>
          </div>
          <iframe title={viewer.filename} src={viewer.url} className="flex-1 w-full rounded-lg bg-white" />
        </div>
      )}
    </div>
  );
}
